import React, { useCallback } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "components/translations/useTranslation";
import OneButtonModalFooter from "components/modals/OneButtonModalFooter";
import { ButtonType } from "components/common/Button.component";
import { MODAL_CONTENT_TAB_INDEX } from "utils/constants";
import { redirectToLogin } from "components/modals/modal.actions";
import * as logger from "utils/logger.function";
import { ErrorMessageProps } from "types/modals";

/**
 * modal used for the global error message (see the showErrorMessage state),
 * in case the session has expired it offers a redirect to the login page instead of a plain close
 */
const ErrorMessage = ({
    more,
    closeModal,
    loopFocus,
    initialFocusRef,
}: ErrorMessageProps): React.ReactElement => {
    const { translate } = useTranslation();
    const dispatch = useDispatch();

    const getErrorText = (): string => {
        if (more?.shouldRedirect) {
            return translate("popUp.error.redirectText");
        }

        if (!more?.statusText) {
            return translate("popUp.error.defaultText");
        }

        return more.applyTranslations ? translate(more.statusText) : more.statusText;
    };

    // the dialog is kept open, so the user still sees the original error and can retry the redirect.
    // escalating to the generic error modal is not an option here - this dialog is driven by the
    // showErrorMessage state, so closing it would unmount the modal router hosting it
    const redirectFailed = useCallback((failure: { error: unknown }) => {
        logger.error("failed to redirect to the login page", failure.error);
    }, []);

    const handleClose = useCallback(() => {
        if (more?.shouldRedirect) {
            // no need to hide the message - the redirect should take place
            dispatch(redirectToLogin(() => undefined, redirectFailed));
            return;
        }

        closeModal();
    }, [more, dispatch, redirectFailed, closeModal]);

    return (
        <>
            <div className="ga-modal__content">
                <div className="modal-body message">{getErrorText()}</div>
            </div>
            <OneButtonModalFooter
                tabIndex={MODAL_CONTENT_TAB_INDEX}
                enabled={true}
                callback={handleClose}
                text={translate("popUp.close")}
                loopFocus={loopFocus}
                buttonRef={initialFocusRef}
                buttonType={ButtonType.PRIMARY}
            />
        </>
    );
};

export default ErrorMessage;
